import amplitude from '../utils/tracking/amplitude';
import mixpanel from '../utils/tracking/mixpanel';
import api from '../utils/api';

import { RESET_PASSWORD_STATUS } from '../constants/auth';
import { ENDPOINT } from '../constants/api';
import { MESSAGE } from '../constants/notifications';

import actions from '../actions';

function* commitPasswordReset(reduction, formData) {
  const state = reduction.setIn(['auth', 'loading'], true);

  yield function effect(dispatch) {
    function onSuccess(response) {
      response.result === 'success'
        ? dispatch(actions.auth.commitPasswordReset.fulfilled())
        : dispatch(actions.auth.commitPasswordReset.rejected(response.error));
    }

    function onFail(error) {
      dispatch(actions.auth.commitPasswordReset.rejected(error));
    }

    api
      .post({ endpoint: ENDPOINT.AUTH, url: '/resetPassword/setPassword', payload: formData })
      .then(onSuccess, onFail);
  };

  return state.hasIn(['auth', 'resetPasswordStatus'])
    ? state
    : state.setIn(['auth', 'resetPasswordStatus'], RESET_PASSWORD_STATUS.COMMIT_REQUESTED);
}

function* commitPasswordResetFulfilled(reduction) {
  return reduction
    .setIn(['auth', 'resetPasswordStatus'], RESET_PASSWORD_STATUS.COMMIT_ACCEPTED)
    .deleteIn(['auth', 'loading']);
}

function* commitPasswordResetRejected(reduction, error) {
  console.error(error);

  return reduction
    .setIn(['auth', 'resetPasswordStatus'], RESET_PASSWORD_STATUS.COMMIT_REJECTED)
    .deleteIn(['auth', 'loading']);
}

function* createAccount(reduction, request) {
  yield function effect(dispatch) {
    function onSuccess(response) {
      response.result === 'success'
        ? dispatch(actions.auth.createAccount.fulfilled(request.payload))
        : dispatch(actions.auth.signIn.rejected(response.data));
    }

    function onFail(error) {
      dispatch(actions.auth.signIn.rejected(error));
    }

    api
      .post({ endpoint: ENDPOINT.AUTH, url: '/createAccount', ...request })
      .then(onSuccess, onFail);
  };

  return reduction.setIn(['auth', 'loading'], true);
}

function* createAccountFulfilled(reduction, payload) {
  yield function effect(dispatch) {
    amplitude.setUserId(payload.email);
    mixpanel.setDistinctId(payload.email);
    amplitude.logEvent('Created an account');
    dispatch(actions.user.getUserData());
    dispatch(actions.auth.setAffiliate());
  };

  return reduction;
}

function* removeError(reduction) {
  return reduction.deleteIn(['auth', 'error']);
}

function* requestPasswordReset(reduction, formData) {
  const state = reduction.setIn(['auth', 'loading'], true);

  yield function effect(dispatch) {
    function onSuccess(response) {
      response.result === 'success'
        ? dispatch(actions.auth.requestPasswordReset.fulfilled())
        : dispatch(actions.auth.requestPasswordReset.rejected(response.error));
    }

    function onFail(error) {
      dispatch(actions.auth.requestPasswordReset.rejected(error));
    }

    api
      .post({ endpoint: ENDPOINT.AUTH, url: '/resetPassword/request', payload: formData })
      .then(onSuccess, onFail);
  };

  return state.hasIn(['auth', 'resetPasswordStatus'])
    ? state
    : state.setIn(['auth', 'resetPasswordStatus'], RESET_PASSWORD_STATUS.QUERY_REQUESTED);
}

function* requestPasswordResetFulfilled(reduction) {
  return reduction
    .setIn(['auth', 'resetPasswordStatus'], RESET_PASSWORD_STATUS.QUERY_ACCEPTED)
    .deleteIn(['auth', 'loading']);
}

function* requestPasswordResetRejected(reduction, error) {
  console.error(error);

  return reduction
    .setIn(['auth', 'resetPasswordStatus'], RESET_PASSWORD_STATUS.QUERY_REJECTED)
    .deleteIn(['auth', 'loading']);
}

function* resetPasswordStatus(reduction, status) {
  return status
    ? reduction.setIn(['auth', 'resetPasswordStatus'], status)
    : reduction.deleteIn(['auth', 'resetPasswordStatus']);
}

function* setAffiliate(reduction, value) {
  return value
    ? reduction.setIn(['auth', 'affiliate'], value)
    : reduction.deleteIn(['auth', 'affiliate']);
}

function* setLoading(reduction, value) {
  const state = reduction.deleteIn(['auth', 'error']);

  return value
    ? state.setIn(['auth', 'loading'], value)
    : state.deleteIn(['auth', 'loading']);
}

function* setLoggedIn(reduction, value) {
  return reduction.setIn(['auth', 'loggedIn'], value);
}

function* signIn(reduction, formData) {
  yield function effect(dispatch) {
    const { email: username, password } = formData;

    function onSuccess(response) {
      if (response.result !== 'success') {
        dispatch(actions.auth.signIn.rejected(response.data));
        return;
      }

      dispatch(actions.user.getUserData());
      dispatch(actions.auth.setAffiliate());
    }

    function onFail(error) {
      dispatch(actions.auth.signIn.rejected(error));
    }

    api
      .post({ endpoint: ENDPOINT.AUTH, url: '/login', payload: { username, password }})
      .then(onSuccess, onFail);
  };

  return reduction.setIn(['auth', 'loading'], true);
}

function* signInRejected(reduction, error) {
  if (typeof error !== 'string') {
    console.error(error);
  }

  return reduction
    .deleteIn(['auth', 'loading'])
    .setIn(['auth', 'error'],
      typeof error === 'string'
        ? error || MESSAGE.FAIL.UNKNOWN
        : MESSAGE.FAIL.UNKNOWN
    );
}

const auth = {
  [actions.auth.commitPasswordReset]: commitPasswordReset,
  [actions.auth.commitPasswordReset.fulfilled]: commitPasswordResetFulfilled,
  [actions.auth.commitPasswordReset.rejected]: commitPasswordResetRejected,
  [actions.auth.createAccount]: createAccount,
  [actions.auth.createAccount.fulfilled]: createAccountFulfilled,
  [actions.auth.removeError]: removeError,
  [actions.auth.requestPasswordReset]: requestPasswordReset,
  [actions.auth.requestPasswordReset.fulfilled]: requestPasswordResetFulfilled,
  [actions.auth.requestPasswordReset.rejected]: requestPasswordResetRejected,
  [actions.auth.resetPasswordStatus]: resetPasswordStatus,
  [actions.auth.setAffiliate]: setAffiliate,
  [actions.auth.setLoading]: setLoading,
  [actions.auth.setLoggedIn]: setLoggedIn,
  [actions.auth.signIn]: signIn,
  [actions.auth.signIn.rejected]: signInRejected,
};

export default auth;
